import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, ThemeProvider, createTheme, Snackbar, Alert, Button } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ParentPortalApp from './pages/ParentPortalApp';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useAppConfig } from './contexts/AppConfigContext';

function AppContent() {
  const { isAuthenticated, loading } = useAuth();
  const { themeColor, loading: configLoading } = useAppConfig();
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  // Service Worker update detection
  useEffect(() => {
    // Check if update is available from localStorage
    if (localStorage.getItem('sw-update-available') === 'true') {
      setUpdateAvailable(true);
      localStorage.removeItem('sw-update-available');
    }

    // Listen for update events
    const handleUpdateAvailable = () => {
      setUpdateAvailable(true);
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable);

    // Check for waiting service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          setUpdateAvailable(true);
        }
      });
    }

    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable);
    };
  }, []);

  const handleUpdate = () => {
    if (waitingWorker) {
      // Tell the waiting service worker to skip waiting and activate
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      setUpdateAvailable(false);
      setWaitingWorker(null);
      // Reload the page
      window.location.reload();
    } else {
      // Force reload to get the new service worker
      window.location.reload();
    }
  };

  // Update theme-color meta tag dynamically based on backend theme
  React.useEffect(() => {
    const themeColorValue = themeColor || '#000';
    
    // Update theme-color meta tag (primary way browsers use theme color)
    const themeMetaTag = document.querySelector('meta[name="theme-color"]');
    if (themeMetaTag) {
      themeMetaTag.setAttribute('content', themeColorValue);
    }

    // Also update apple-mobile-web-app-status-bar-style for iOS
    const appleStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (appleStatusBar) {
      // Use black-translucent for better visibility with custom colors
      appleStatusBar.setAttribute('content', 'black-translucent');
    }
  }, [themeColor]);

  // Create dynamic theme based on config
  const dynamicTheme = React.useMemo(() => {
    return createTheme({
      palette: {
        primary: {
          main: themeColor || '#115263',
          light: '#42a5f5',
          dark: '#1565c0',
          contrastText: '#fff',
        },
        secondary: {
          main: '#dc004e',
          light: '#ff5983',
          dark: '#9a0036',
          contrastText: '#fff',
        },
        background: {
          default: '#f5f5f5',
          paper: '#ffffff',
        },
        text: {
          primary: '#333333',
          secondary: '#666666',
        },
      },
      typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h1: { fontSize: '2.5rem', fontWeight: 500 },
        h2: { fontSize: '2rem', fontWeight: 500 },
        h3: { fontSize: '1.75rem', fontWeight: 500 },
        h4: { fontSize: '1.5rem', fontWeight: 500 },
        h5: { fontSize: '1.25rem', fontWeight: 500 },
        h6: { fontSize: '1rem', fontWeight: 500 },
      },
      shape: { borderRadius: 8 },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 8,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              borderRadius: 12,
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            },
          },
        },
      },
    });
  }, [themeColor]);

  if (loading || configLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={dynamicTheme}>
      <Box
        sx={{
          width: '100%',
          minHeight: '100vh',
          bgcolor: 'grey.200',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '450px',
            minHeight: '100vh',
            bgcolor: 'background.default',
            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
          }}
        >
          <Routes>
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/login" element={isAuthenticated ? <Navigate to="/my-child" replace /> : <Login />} />
            <Route
              path="/my-child/*"
              element={isAuthenticated ? <ParentPortalApp /> : <Navigate to="/landing" replace />}
            />
            <Route path="/" element={<Navigate to="/landing" replace />} />
          </Routes>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          
          {/* Service Worker Update Notification */}
          <Snackbar
            open={updateAvailable}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{ bottom: { xs: 90, sm: 24 } }}
          >
            <Alert
              severity="info"
              action={
                <Button color="inherit" size="small" onClick={handleUpdate} sx={{ fontWeight: 700 }}>
                  Update Now
                </Button>
              }
              sx={{ 
                width: '100%',
                alignItems: 'center',
                '& .MuiAlert-message': {
                  flex: 1,
                },
              }}
            >
              A new version is available. Please update to get the latest features.
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;

